/* eslint-disable no-underscore-dangle */
import lod_ from "lodash";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { display } from "redux-react/reducers/snackBarReducer";
import actions from "redux-react/actions/sourceActions";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import { t } from "i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import {
	Close as CloseIcon,
	Fullscreen as FullscreenIcon,
	FullscreenExit
} from "@mui/icons-material";
import { createAsset, updateAsset } from "../../../../redux-react/reducers/assetReducer";
import { deleteFileFromPath } from "../../../../helpers/s3";
import LanguageInput from "./LanguageInput";
import { statusLabels, FILE_TYPE_HTML, PRIVATE } from "../../../../contants";
import { useLanguages } from "hooks/useLanguages";

const initialValue = {
	name: "",
	description: "",
	fileType: "",
	status: "draft",
	content: [],
	isFullScreen: false,
	access: PRIVATE
};

const DialogCreateAsset = ({
	folderId,
	assistantID,
	asset,
	isEdit = false,
	onClose,
	getFolders = () => {},
	acceptedFile
}) => {
	const dispatch = useDispatch();
	const [assetValues, setAssetValues] = useState(initialValue);
	const [isLoading, setIsLoading] = useState(false);
	const [newUploadedS3Keys, setNewUploadedS3Keys] = useState([]);
	const [existingS3Keys, setExistingS3Keys] = useState([]);
	const { languages } = useLanguages();

	const isAssetThemme = asset?.fileType === "theme" || asset?.key === "theme";

	useEffect(() => {
		if (isEdit && asset) {
			const updatedValue = {
				name: asset.name || "",
				description: asset.description || "",
				fileType: asset.fileType || "",
				status: asset.status || "draft",
				content: asset?.content || [],
				access: asset?.access || PRIVATE,
				isFullScreen: false
			};
			setAssetValues(updatedValue);

			const existingKeys = [];
			asset?.content?.forEach(content => {
				Object.values(content).forEach(langContent => {
					langContent?.attachments?.forEach(attachment => {
						if (attachment?.key) existingKeys.push(attachment.key);
					});
				});
			});
			setExistingS3Keys(existingKeys);
		}
	}, [isEdit, asset]);

	useEffect(() => {
		if (
			acceptedFile &&
			(acceptedFile === "images" || acceptedFile === "videos" || acceptedFile === "documents")
		) {
			setAssetValues(prev => ({
				...prev,
				fileType: acceptedFile
			}));
		} else if (acceptedFile === "internal_pages") {
			setAssetValues(prev => ({
				...prev,
				fileType: "htmlText"
			}));
		} else if (acceptedFile === "css") {
			setAssetValues(prev => ({
				...prev,
				fileType: "css"
			}));
		} else if (isAssetThemme) {
			setAssetValues(prev => ({
				...prev,
				fileType: "theme"
			}));
		}
	}, [acceptedFile]);

	const handleCleanupAfterCancel = useCallback(
		async s3Keys => {
			if (s3Keys.length === 0) return;

			if (s3Keys.length > 0) {
				const deleteResponse = await deleteFileFromPath({ key: s3Keys[0] });
				if (!deleteResponse.success) throw new Error("Failed to delete file from S3");
			}
		},
		[dispatch]
	);

	const handleCancel = () => {
		handleCleanupAfterCancel(newUploadedS3Keys);
		onClose();
	};

	const formik = useFormik({
		initialValues: assetValues,
		enableReinitialize: true,
		validate: values => {
			const errors = {};
			if (!isEdit) {
				if (!values.name) errors.name = t("SOURCES.FILES.required");
				if (!values.fileType) errors.fileType = t("SOURCES.FILES.required");
			}

			switch (values.fileType) {
				case "htmlText": {
					const content = values?.content?.[0] || {};

					let atLeastOneNotEmpty = false;
					Object.values(content).forEach(langContent => {
						// replace <br> and <p></p> tags with empty string and trim whitespace
						const temporaryHtmlContent = (langContent?.htmlText || "")
							?.replace(/<br\s*\/?>/gi, "")
							.replace(/<p><\/p>/gi, "")
							.trim();

						const isEmpty = lod_.isNil(temporaryHtmlContent) || lod_.isEmpty(temporaryHtmlContent);

						if (!isEmpty) atLeastOneNotEmpty = true;
					});

					if (!atLeastOneNotEmpty) {
						errors.content = t("SOURCES.FILES.required");
					}
					break;
				}
				default: {
					break;
				}
			}

			return errors;
		},
		onSubmit: async values => {
			setIsLoading(true);
			try {
				const newAsset = {
					...asset,
					folderId,
					assistantID,
					...values,
					fileType: !isEdit && values.fileType === "document" ? acceptedFile : values.fileType
				};
				const onSuccessCallback = res => {
					setIsLoading(false);
					dispatch(
						display({
							type: "success",
							message: isEdit ? t("SOURCES.FILES.updateSuccess") : t("SOURCES.FILES.uploadSuccess")
						})
					);
					if (isEdit) {
						dispatch(updateAsset({ ...res, id: res._id }));
					} else {
						dispatch(createAsset({ ...res?.result, id: res?.result?._id }));
					}
					getFolders();
					setNewUploadedS3Keys([]); // Réinitialise après succès
					onClose();
				};

				if (isEdit) {
					dispatch(actions.updateAsset(newAsset._id, newAsset, onSuccessCallback));
				} else {
					dispatch(actions.createAsset({ asset: newAsset }, assistantID, onSuccessCallback));
				}
			} catch (error) {
				await handleCancel();
				setIsLoading(false);
				dispatch(
					display({
						type: "error",
						message: isEdit ? t("SOURCES.FILES.updateError") : t("SOURCES.FILES.uploadError")
					})
				);
			}
		}
	});

	const hasFormChanged = () => {
		if (!isEdit) return false;
		return Object.keys(formik.values).some(
			key => JSON.stringify(formik.values[key]) !== JSON.stringify(assetValues[key])
		);
	};

	const isCreateButtonDisabled = () => {
		if (isLoading) return true;
		if (isEdit) return !hasFormChanged();

		const hasFormikErrors = Object.keys(formik.errors).length > 0;
		if (hasFormikErrors) return true;

		return !formik.values.name || !formik.values.fileType;
	};

	const toggleFullScreen = () => {
		formik.setFieldValue("isFullScreen", !formik.values.isFullScreen);
	};

	const onChangeTypologyInput = (language, name, value) => {
		formik.setFieldValue("content", [
			{
				...formik.values.content?.[0],
				[language]: {
					///	...formik.values.content?.[0]?.[language],
					[name]: value
				}
			}
		]);
	};

	const handleFileUploaded = s3Key => {
		if (!existingS3Keys.includes(s3Key)) {
			setNewUploadedS3Keys(prev => [...prev, s3Key]);
		}
	};

	return (
		<Dialog
			open
			maxWidth={formik.values.isFullScreen ? false : "md"}
			fullWidth
			fullScreen={formik.values.isFullScreen || formik.values.fileType === FILE_TYPE_HTML}
			PaperProps={{
				sx: { borderRadius: "10px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)" }
			}}
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					fontSize: "1.25rem",
					fontWeight: "500",
					borderBottom: "1px solid #e0e0e0",
					padding: "16px 24px"
				}}
			>
				{isEdit ? t("SOURCES.FILES.editTitle") : t("SOURCES.FILES.uploadTitle")}
				<Box>
					<Tooltip
						title={
							formik.values.isFullScreen
								? t("ASSETS.DETAILS.reduce")
								: t("ASSETS.DETAILS.fullscreen")
						}
						arrow
					>
						<IconButton onClick={toggleFullScreen} size="small" sx={{ mr: 1 }}>
							{formik.values.isFullScreen ? <FullscreenExit /> : <FullscreenIcon />}
						</IconButton>
					</Tooltip>
					<IconButton onClick={handleCancel} size="small">
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent sx={{ padding: "24px" }}>
				<MDBox display="flex" flexDirection="column" gap={3} sx={{ mt: 2 }}>
					<MDBox display="flex" flexDirection="row">
						<MDBox flex={10}>
							<TextField
								fullWidth
								name="name"
								label={t("SOURCES.FILES.name")}
								value={formik.values.name}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={formik.touched.name && Boolean(formik.errors.name)}
								helperText={formik.touched.name && formik.errors.name}
								variant="outlined"
								type="text"
								required
								disabled={isLoading}
							/>
						</MDBox>

						{!isAssetThemme && (
							<MDBox ml={1} flex={1}>
								<FormControl fullWidth disabled={isLoading}>
									<InputLabel>{t("SOURCES.FILES.status")}</InputLabel>
									<Select
										name="status"
										value={formik.values.status}
										onChange={formik.handleChange}
										label={t("SOURCES.FILES.status")}
									>
										{statusLabels.map(stat => (
											<MenuItem key={stat?.value} value={stat.value}>
												{t(stat?.label)}
											</MenuItem>
										))}
									</Select>
								</FormControl>
							</MDBox>
						)}
					</MDBox>

					{!isAssetThemme &&
						languages?.map(it => (
							<LanguageInput
								key={it.code}
								languageName={it.language}
								typology={
									formik.values.fileType === "images" ||
									formik.values.fileType === "videos" ||
									formik.values.fileType === "documents" ||
									formik.values.fileType === "css"
										? "document"
										: formik.values.fileType
								}
								onChangeTypologyInput={onChangeTypologyInput}
								codeLanguage={it?.code}
								value={formik.values.content?.[0]?.[it?.code]}
								isEdit={isEdit}
								disabled={isLoading}
								onFileUploaded={handleFileUploaded}
								url={it?.logoURL}
								codeLanguagePosibles={languages?.map(it => it.code)}
								acceptedFile={acceptedFile || asset.fileType}
							/>
						))}
				</MDBox>
			</DialogContent>
			<DialogActions sx={{ borderTop: "1px solid #e0e0e0" }}>
				<MDButton onClick={handleCancel} variant="outlined" color="info" disabled={isLoading}>
					{t("SOURCES.FILES.cancel")}
				</MDButton>
				<MDButton
					onClick={() => formik.handleSubmit()}
					type="submit"
					color="info"
					variant="contained"
					disabled={isCreateButtonDisabled()}
					sx={{ position: "relative" }}
				>
					{isLoading && (
						<CircularProgress
							size={24}
							sx={{
								position: "absolute",
								left: "50%",
								top: "50%",
								marginLeft: "-12px",
								marginTop: "-12px"
							}}
						/>
					)}
					<span style={{ visibility: isLoading ? "hidden" : "visible" }}>
						{isEdit ? t("SOURCES.FILES.update") : t("SOURCES.FILES.create")}
					</span>
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default DialogCreateAsset;
